import orderBy from 'lodash/orderBy';
import type { IOdds } from '@/interface/IOdds';
import type { IBetBuilderMarket } from '@/interface/betBuilder';
import { MarketType } from '@/interface/enum';
import { toFixedDecimalPoint } from '@/libraries/utils/text';

const invertedSignMarketTypes = [MarketType.Handicap, MarketType.FH_Handicap];

function toPointKey(point: number) {
    return toFixedDecimalPoint(point, 2);
}

export function sortBetBuilderMarketsByRecordedOrder(
    markets: IBetBuilderMarket[],
    recordedPoints: string[] | undefined,
    marketType?: MarketType,
): IBetBuilderMarket[] {
    if (!recordedPoints || recordedPoints.length === 0) {
        return orderBy(markets, market => market.point);
    }

    const invertSign = marketType !== undefined && invertedSignMarketTypes.includes(marketType);
    const toNormalPoint = (market: IBetBuilderMarket) => (invertSign ? -market.point : market.point);

    return orderBy(
        markets,
        [
            (market) => {
                const rank = recordedPoints.indexOf(toPointKey(toNormalPoint(market)));
                return rank === -1 ? Infinity : rank;
            },
            toNormalPoint,
        ],
    );
}

export function sortBetBuilderMarketsByDisplayPriority(markets: IBetBuilderMarket[]): IBetBuilderMarket[] {
    return orderBy(markets, ['displayPriority', 'point'], ['desc', 'asc']);
}

export function sortBetBuilderMarketsForDisplay(
    markets: IBetBuilderMarket[],
    isDisplayPriorityEnabled: boolean,
    fallbackSort: (markets: IBetBuilderMarket[]) => IBetBuilderMarket[],
): IBetBuilderMarket[] {
    return isDisplayPriorityEnabled
        ? sortBetBuilderMarketsByDisplayPriority(markets)
        : fallbackSort(markets);
}

export function buildMarketPointOrder(odds: IOdds[]): Map<MarketType, string[]> {
    const order = new Map<MarketType, string[]>();

    odds.forEach((item) => {
        const key = toPointKey(item.point);
        const points = order.get(item.marketType) ?? [];
        if (!points.includes(key)) {
            points.push(key);
        }
        order.set(item.marketType, points);
    });

    return order;
}
